// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import styles from '@app/features/app/components/dialogs/components/SettingsModalHeader.module.css';
import {SettingsHeadingLinkButton} from '@app/features/app/components/dialogs/shared/SettingsHeadingLinkButton';
import settingsModalStyles from '@app/features/app/components/dialogs/shared/SettingsModalLayout.module.css';
import {UnsavedChangesBannerContent} from '@app/features/app/components/dialogs/shared/UnsavedChangesBannerContent';
import {NativeDragRegion} from '@app/features/app/components/layout/NativeDragRegion';
import {CLOSE_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {XIcon} from '@app/features/ui/icons/Icons';
import type {TabData} from '@app/features/ui/state/UnsavedChanges';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {AnimatePresence, motion} from 'framer-motion';
import {observer} from 'mobx-react-lite';
import type React from 'react';

interface SettingsModalHeaderProps {
	title: React.ReactNode;
	/** One muted line under the title. Only shown by the hero variant. */
	subtitle?: React.ReactNode;
	pageLinkHref?: string | null;
	showUnsavedBanner: boolean;
	flashBanner: boolean;
	tabData: TabData;
	onClose: () => void;
	/**
	 * `bar` (default): the thin sticky title bar. `hero` (round 6 user settings): a big page
	 * title with a one-line subtitle, the close button as a round tonal button top-right,
	 * and the unsaved-changes banner as a floating pill.
	 */
	variant?: 'bar' | 'hero';
	/** KONVO: page-level buttons beside the hero title (What Konvo knows: History). Hero only. */
	actions?: React.ReactNode;
}

const UnsavedBanner: React.FC<{tabData: TabData; flashBanner: boolean}> = ({tabData, flashBanner}) => (
	<UnsavedChangesBannerContent
		tabData={tabData}
		textContainerClassName={styles.bannerTextContainer}
		textClassName={clsx(styles.bannerText, flashBanner ? styles.bannerTextFlash : styles.bannerTextNormal)}
		actionsClassName={styles.bannerActions}
		smallActions={true}
		dataFlx={{
			textContainer: 'app.settings-modal-header.banner-text-container',
			text: 'app.settings-modal-header.banner-text',
			actions: 'app.settings-modal-header.banner-actions',
			resetButton: 'app.settings-modal-header.button.reset',
			saveButton: 'app.settings-modal-header.button.save',
		}}
		data-flx="app.settings-modal-header.unsaved-changes-banner-content"
	/>
);

const HeroSettingsModalHeader: React.FC<Omit<SettingsModalHeaderProps, 'variant'>> = observer(
	({title, subtitle, pageLinkHref, showUnsavedBanner, flashBanner, tabData, onClose, actions}) => {
		const {i18n} = useLingui();
		const prefersReducedMotion = Accessibility.useReducedMotion;
		const fade = {
			initial: prefersReducedMotion ? {opacity: 1} : {opacity: 0},
			animate: {opacity: 1},
			exit: prefersReducedMotion ? {opacity: 1} : {opacity: 0},
			transition: prefersReducedMotion ? {duration: 0} : {duration: 0.2, ease: 'easeOut' as const},
		};
		return (
			<NativeDragRegion className={styles.hero} data-flx="app.settings-modal-header.hero">
				<div className={styles.heroInner} data-flx="app.settings-modal-header.hero-inner">
					<div className={styles.heroTitleRow} data-flx="app.settings-modal-header.hero-title-row">
						<h1 className={styles.heroTitle} data-flx="app.settings-modal-header.title">
							{title}
						</h1>
						{pageLinkHref ? (
							<SettingsHeadingLinkButton
								href={pageLinkHref}
								target="page"
								data-flx="app.settings-modal-header.heading-link-button"
							/>
						) : null}
						{actions ? (
							<div className={styles.heroActions} data-flx="app.settings-modal-header.hero-actions">
								{actions}
							</div>
						) : null}
					</div>
					{subtitle ? (
						<p className={styles.heroSubtitle} data-flx="app.settings-modal-header.subtitle">
							{subtitle}
						</p>
					) : null}
				</div>
				<AnimatePresence data-flx="app.settings-modal-header.animate-presence">
					{showUnsavedBanner ? (
						<motion.div
							key="banner"
							{...fade}
							className={clsx(styles.heroBanner, flashBanner && styles.heroBannerFlash)}
							data-flx="app.settings-modal-header.banner-content"
						>
							<UnsavedBanner tabData={tabData} flashBanner={flashBanner} />
						</motion.div>
					) : null}
				</AnimatePresence>
				<button
					type="button"
					aria-label={i18n._(CLOSE_DESCRIPTOR)}
					onClick={onClose}
					className={styles.heroClose}
					data-flx="app.settings-modal-header.button.close"
				>
					<XIcon weight="bold" className={styles.heroCloseIcon} data-flx="app.settings-modal-header.icon" />
				</button>
			</NativeDragRegion>
		);
	},
);

export const SettingsModalHeader: React.FC<SettingsModalHeaderProps> = observer(
	({title, subtitle, pageLinkHref, showUnsavedBanner, flashBanner, tabData, onClose, variant = 'bar', actions}) => {
		const {i18n} = useLingui();
		const prefersReducedMotion = Accessibility.useReducedMotion;
		if (variant === 'hero') {
			return (
				<HeroSettingsModalHeader
					title={title}
					subtitle={subtitle}
					pageLinkHref={pageLinkHref}
					showUnsavedBanner={showUnsavedBanner}
					flashBanner={flashBanner}
					tabData={tabData}
					onClose={onClose}
					actions={actions}
				/>
			);
		}
		return (
			<NativeDragRegion
				className={`${settingsModalStyles.desktopHeader} ${styles.headerTransition}`}
				style={{
					transitionDuration: prefersReducedMotion ? '0ms' : '200ms',
					backgroundColor:
						showUnsavedBanner && flashBanner
							? 'var(--status-danger)'
							: showUnsavedBanner
								? 'var(--background-primary)'
								: undefined,
				}}
				data-flx="app.settings-modal-header.header-transition"
			>
				<AnimatePresence mode="wait" data-flx="app.settings-modal-header.animate-presence">
					{showUnsavedBanner ? (
						<motion.div
							key="banner"
							initial={prefersReducedMotion ? {opacity: 1} : {opacity: 0}}
							animate={{opacity: 1}}
							exit={prefersReducedMotion ? {opacity: 1} : {opacity: 0}}
							transition={prefersReducedMotion ? {duration: 0} : {duration: 0.25, ease: 'easeOut'}}
							className={styles.bannerContent}
							data-flx="app.settings-modal-header.banner-content"
						>
							<UnsavedBanner tabData={tabData} flashBanner={flashBanner} />
						</motion.div>
					) : (
						<motion.div
							key="title"
							initial={prefersReducedMotion ? {opacity: 1} : {opacity: 0}}
							animate={{opacity: 1}}
							exit={prefersReducedMotion ? {opacity: 1} : {opacity: 0}}
							transition={prefersReducedMotion ? {duration: 0} : {duration: 0.25, ease: 'easeOut'}}
							className={styles.titleContent}
							data-flx="app.settings-modal-header.title-content"
						>
							<div className={styles.titleWrapper} data-flx="app.settings-modal-header.title-wrapper">
								<h1 className={styles.title} data-flx="app.settings-modal-header.title">
									{title}
								</h1>
								{pageLinkHref ? (
									<SettingsHeadingLinkButton
										href={pageLinkHref}
										target="page"
										data-flx="app.settings-modal-header.heading-link-button"
									/>
								) : null}
							</div>
							<button
								type="button"
								aria-label={i18n._(CLOSE_DESCRIPTOR)}
								onClick={onClose}
								className={settingsModalStyles.closeButton}
								data-flx="app.settings-modal-header.button.close"
							>
								<XIcon weight="bold" className={styles.icon} data-flx="app.settings-modal-header.icon" />
							</button>
						</motion.div>
					)}
				</AnimatePresence>
			</NativeDragRegion>
		);
	},
);
